import axios from 'axios';
import { API_BASE_URL } from './apiBase';

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 1800000, // 30 min – Gemini grounding tools process each prompt sequentially
    headers: {
        'Content-Type': 'application/json'
    }
});

// ── Processing-log API trace ─────────────────────────────────────────
// While a record is being processed, the pipeline registers a recorder. Every API
// call made meanwhile asks the backend for a trace (x-api-trace) and is written to
// that record's processing log: request sent, response received, and the external
// calls (AI prompts, search, scrape) the backend made to serve it.
let activeTraceRecorder = null;

// Detailed API logs (prompts, raw responses) are for super admins only.
// The backend enforces the same rule and returns no trace to other roles.
const isSuperAdminSession = () => {
    try {
        return JSON.parse(localStorage.getItem('banyan_user') || 'null')?.role === 'super_admin';
    } catch {
        return false;
    }
};

export const setApiTraceRecorder = (recorder) => {
    activeTraceRecorder = typeof recorder === 'function' && isSuperAdminSession() ? recorder : null;
};

const MAX_LOG_STRING = 40000;
const capStrings = (value, depth = 0) => {
    if (typeof value === 'string') {
        return value.length > MAX_LOG_STRING
            ? `${value.slice(0, MAX_LOG_STRING)}\n…[truncated ${value.length - MAX_LOG_STRING} chars]`
            : value;
    }
    if (!value || typeof value !== 'object' || depth > 12) return value;
    if (Array.isArray(value)) return value.map(v => capStrings(v, depth + 1));
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, capStrings(v, depth + 1)]));
};

const parseRequestData = (data) => {
    if (typeof data !== 'string') return data ?? null;
    try { return JSON.parse(data); } catch { return data; }
};

const splitTrace = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data) || !('_trace' in data)) {
        return { body: data, externalCalls: [] };
    }
    const { _trace, ...body } = data;
    return { body, externalCalls: Array.isArray(_trace) ? _trace : [] };
};

const recordApiCall = (config, { status, data, error }) => {
    const trace = config?.__apiTrace;
    if (!trace) return;
    const { body, externalCalls } = splitTrace(data);
    const method = (config.method || 'get').toUpperCase();
    const ok = !error && status >= 200 && status < 300 && body?.success !== false;
    try {
        trace.recorder({
            stage: 'API',
            action: `${method} ${config.url}`,
            result: ok ? 'success' : 'error',
            timestamp: new Date().toISOString(),
            data: {
                method,
                endpoint: config.url,
                params: config.params || null,
                status: status ?? null,
                startedAt: new Date(trace.startedAt).toISOString(),
                durationMs: Date.now() - trace.startedAt,
                request: capStrings(parseRequestData(config.data)),
                response: capStrings(body ?? null),
                error: error || body?.error || null,
                externalCalls,
            },
        });
    } catch (recordError) {
        console.warn('[API trace] Could not record call:', recordError);
    }
};

// Request interceptor — attach JWT token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('banyan_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        if (activeTraceRecorder && !config.__apiTrace) {
            config.headers['x-api-trace'] = '1';
            config.__apiTrace = { recorder: activeTraceRecorder, startedAt: Date.now() };
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Delay helper
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Response interceptor with global retry logic for 429 Too Many Requests
api.interceptors.response.use(
    (response) => {
        recordApiCall(response.config, { status: response.status, data: response.data });
        return splitTrace(response.data).body;
    },
    async (error) => {
        const config = error.config;

        // If we got a 429 (Rate Limit Exceeded) and haven't retried too many times
        if (error.response && error.response.status === 429) {
            config._retryCount = config._retryCount || 0;

            if (config._retryCount < 3) {
                config._retryCount += 1;
                // Exponential backoff: 2s, 4s, 8s
                const delay = Math.pow(2, config._retryCount) * 1000;
                console.warn(`[API] Rate limited (429). Retrying in ${delay}ms... (Attempt ${config._retryCount})`);
                await sleep(delay);
                return api(config);
            }
        }

        // Handle 401 Unauthorized — redirect to login
        if (error.response && error.response.status === 401) {
            // Don't redirect if already on login page or if this is the login request itself
            const isLoginRequest = error.config?.url?.includes('/auth/login');
            if (!isLoginRequest && !window.location.pathname.includes('/login')) {
                localStorage.removeItem('banyan_token');
                localStorage.removeItem('banyan_user');
                window.location.href = '/login';
                return Promise.reject(new Error('Session expired. Please login again.'));
            }
        }

        const message = error.response?.data?.error || error.response?.data?.message || error.message || 'An error occurred';
        recordApiCall(error.config, { status: error.response?.status, data: error.response?.data, error: message });
        return Promise.reject(new Error(message));
    }
);

/**
 * API Methods
 */

export const apiClient = {
    // Health check
    health: () => api.get('/health'),
    testGemini: () => api.get('/test-gemini'),

    // Source DB
    checkSource: (items) => api.post('/check-source', { items }),

    // Prompt processing (ChatGPT / other non-batch providers)
    processPromptsWithChatGPT: (data) => api.post('/chatgpt/process-prompts', data),

    // Gemini Instant (per-prompt, provider='gemini')
    processPromptsWithGemini: (data) => api.post('/chatgpt/process-prompts', { ...data, provider: 'gemini' }),

    // ── Gemini Batch API ─────────────────────────────────────
    submitGeminiBatch: (data) => api.post('/gemini-batch/submit', data),
    checkGeminiBatchStatus: (batchJobName) => api.get(`/gemini-batch/status/${batchJobName}`),
    fetchGeminiBatchResults: (batchJobName) => api.get(`/gemini-batch/results/${batchJobName}`),
    retryGeminiBatch: (data) => api.post('/gemini-batch/retry', data),

    // Major table persistence (Banyan_V2)
    // body: { record: {flat record}, forceOverwrite?: bool, existingId?: number }
    saveMajorRecord: (body) => api.post('/major-records/save', body),
    importMajorRecords: (records, overwriteDuplicates = false) =>
        api.post('/major-records/import', { records, overwriteDuplicates }),

    // Major table list (paginated, searchable, filterable) — _t prevents 304 stale cache
    listMajorRecords: ({ page = 1, limit = 50, search = '', sortField = 'id', sortDir = 'desc', filterPartNumber = '', filterManufacturer = '', filterVendor = '' } = {}) =>
        api.get('/major-records/list', { params: { page, limit, search, sortField, sortDir, filterPartNumber, filterManufacturer, filterVendor, _t: Date.now() } }),

    // Major table batch lookup by part numbers
    lookupMajorRecords: (partNumbers) => api.post('/major-records/lookup', { partNumbers }),

    // Major table batch delete by part numbers
    deleteMajorRecords: (partNumbers) => api.post('/major-records/delete', { partNumbers }),

    // Major table delete single record by ID
    deleteMajorRecordById: (id) => api.delete(`/major-records/${id}`),
    bulkDeleteMajorRecordsById: (ids) => api.post('/major-records/bulk-delete', { ids }),

    // Check if PN exists in Master DB (across all PN columns)

    checkPartNumber: (partNumber) => api.post('/major-records/check-pn', { partNumber }),

    // BU Types
    getActiveBUPartAValues: () => api.get('/bu-types/active'),

    // ── Enrichment Pipeline ──────────────────────────────────
    enrichSingle: (data) => api.post('/enrichment/single', data),
    enrichBatch: (data) => api.post('/enrichment/batch', data),
    getEnrichmentJob: (jobId) => api.get(`/enrichment/job/${jobId}`),
    getEnrichmentJobs: (params) => api.get('/enrichment/jobs', { params }),

    // ── Step-by-Step Enrichment (manual verification) ────────
    stepSearch: (data) => api.post('/enrichment/step/search', data),
    stepSearchValidated: (data) => api.post('/enrichment/step/search-validated', data),
    agentRecoverUrls: (data) => api.post('/enrichment/agent/recover-urls', data),
    stepMatchDbManufacturers: (data) => api.post('/enrichment/step/match-db-manufacturers', data),
    stepRank: (data) => api.post('/enrichment/step/rank', data),
    stepRankPreview: (data) => api.post('/enrichment/step/rank/preview', data),
    stepManufacturerValidate: (data) => api.post('/enrichment/step/manufacturer-validate', data),
    stepScrape: (data) => api.post('/enrichment/step/scrape', data),
    stepDeepClean: (data) => api.post('/enrichment/step/deep-clean', data),
    stepCleanExtract: (data) => api.post('/enrichment/step/clean-extract', data),
    stepPrepareExtract: (data) => api.post('/enrichment/step/prepare-extract', data),
    stepExtract: (data) => api.post('/enrichment/step/extract', data),
    stepExtractFromDescription: (data) => api.post('/enrichment/step/extract-from-description', data),
    stepModelExtract: (data) => api.post('/enrichment/step/model-extract', data),
    stepExtractWithMemory: (data) => api.post('/enrichment/step/extract-with-memory', data),
    savePropertyConditions: (data) => api.post('/property-conditions/save', data),

    // Property Conditions page (Data Center)
    listPropertyConditions: (params = {}) => api.get('/property-conditions', { params: { ...params, _t: Date.now() } }),
    createPropertyCondition: (data) => api.post('/property-conditions', data),
    updatePropertyCondition: (id, data) => api.put(`/property-conditions/${id}`, data),
    deletePropertyConditions: (ids) => api.post('/property-conditions/delete', { ids }),
    importPropertyConditions: (rows) => api.post('/property-conditions/import', { rows }),

    // ── Model Mapper ─────────────────────────────────────────
    modelMapperHealth: () => api.get('/model-mapper/health'),
    modelMapperClearCache: () => api.post('/model-mapper/clear-cache'),

    // ── Training Data Capture ────────────────────────────────
    saveTrainingPair: (data) => api.post('/training-data/save', data),
    trainingDataStats: () => api.get('/training-data/stats'),
    trainingDataExport: (params) => api.get('/training-data/export', { params }),
    approveTrainingData: (data) => api.post('/training-data/approve', data),

    // ── Training Dashboard ──────────────────────────────────────
    processExcelRow: (data) => api.post('/training/process-excel-row', data),
    startTraining: (data) => api.post('/training/start', data),
    trainingStatus: () => api.get('/training/status'),
    resetTrainingRecords: () => api.delete('/training/records'),
    resetCleanedContent: () => api.delete('/training/cleaned-content'),
    resetTrainedModel: () => api.delete('/training/model'),

    // ── Model Version Management ─────────────────────────────────
    listModelVersions: () => api.get('/model-versions'),
    createModelVersion: (data) => api.post('/model-versions', data),
    getModelVersion: (id) => api.get(`/model-versions/${id}`),
    updateModelVersion: (id, data) => api.put(`/model-versions/${id}`, data),
    deleteModelVersion: (id) => api.delete(`/model-versions/${id}`),
    activateModelVersion: (id) => api.post(`/model-versions/${id}/activate`),
    getModelVersionStats: (id) => api.get(`/model-versions/${id}/stats`),
    getModelVersionRecords: (id, page = 1, limit = 50) => api.get(`/model-versions/${id}/records`, { params: { page, limit } }),
    exportColabData: async (id) => {
        // Use authenticated axios call (window.open doesn't carry JWT token)
        const response = await axios.get(`${API_BASE_URL}/model-versions/${id}/export-colab`, {
            responseType: 'blob',
            headers: {
                Authorization: `Bearer ${localStorage.getItem('banyan_token')}`,
            },
        });

        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = `training_data_v${id}_${new Date().toISOString().slice(0, 10)}.jsonl`;
        if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (match) filename = match[1];
        }

        // Trigger browser download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: true, filename };
    },

    // ── Training Record Management ───────────────────────────
    deleteTrainingRecord: (id) => api.delete(`/training-data/${id}`),
    updateTrainingRecord: (id, data) => api.put(`/training-data/${id}`, data),
    reprocessTrainingRecord: (id) => api.post(`/training-data/${id}/reprocess`),

    // ── Noun/Modifier Master Data ────────────────────────────
    nounModifierList: (params) => api.get('/noun-modifier', { params }),
    nounModifierStats: () => api.get('/noun-modifier/stats'),
    nounModifierCheckDuplicate: (data) => api.post('/noun-modifier/check-duplicate', data),
    nounModifierBatchLookup: (partNumbers) => api.post('/noun-modifier/batch-lookup', { partNumbers }),
    nounModifierCreate: (data) => api.post('/noun-modifier', data),
    nounModifierUpdate: (id, data) => api.put(`/noun-modifier/${id}`, data),
    nounModifierDelete: (id) => api.delete(`/noun-modifier/${id}`),
    nounModifierBulkImport: (records) => api.post('/noun-modifier/bulk-import', { records }),
    nounModifierBulkDelete: (ids) => api.post('/noun-modifier/bulk-delete', { ids }),
    nounModifierExport: (search) => api.get('/noun-modifier/export', { params: { search } }),
    nounModifierSuggest: (q) => api.get('/noun-modifier/suggest', { params: { q } }),
    nounModifierGetById: (id) => api.get(`/noun-modifier/by-id/${id}`),

    // ── Property Prompt Configuration ─────────────────────────────
    propertyPromptList: (params) => api.get('/property-prompts', { params }),
    propertyPromptStats: () => api.get('/property-prompts/stats'),
    propertyPromptCreate: (data) => api.post('/property-prompts', data),
    propertyPromptUpdate: (id, data) => api.put(`/property-prompts/${id}`, data),
    propertyPromptDelete: (id) => api.delete(`/property-prompts/${id}`),
    propertyPromptBatchLookup: (propertyNames) => api.post('/property-prompts/batch-lookup', { propertyNames }),
    propertyPromptMerge: (data) => api.post('/property-prompts/merge', data),
    propertyPromptBulkImport: (records) => api.post('/property-prompts/bulk-import', { records }),
    propertyPromptBulkDelete: (ids) => api.post('/property-prompts/bulk-delete', { ids }),
    propertyPromptExport: (search) => api.get('/property-prompts/export', { params: { search } }),

    // ── Auth ──────────────────────────────────────────────────
    authLogin: (username, password, captchaToken) => api.post('/auth/login', { username, password, hcaptcha_token: captchaToken }),
    authMe: () => api.get('/auth/me'),
    authChangePassword: (old_password, new_password) => api.put('/auth/change-password', { old_password, new_password }),

    // ── User Management ──────────────────────────────────────
    getUsers: () => api.get('/users'),
    createUserAccount: (data) => api.post('/users', data),
    updateUserAccount: (id, data) => api.put(`/users/${id}`, data),
    deleteUserAccount: (id) => api.delete(`/users/${id}`),
    resetUserPassword: (id, new_password) => api.put(`/users/${id}/password`, { new_password }),

    // ── Permissions ──────────────────────────────────────────
    getPermissions: () => api.get('/permissions'),
    getPermissionsForRole: (role) => api.get(`/permissions/${role}`),
    updatePermissionsForRole: (role, permissions) => api.put(`/permissions/${role}`, { permissions }),

    // ── QA Comparison ────────────────────────────────────────
    qaCompare: (actualData, predictedData, model) => api.post('/qa/compare', { actualData, predictedData, model }),
};

export default api;
